import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getData, putData } from '../Services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const ViewPO = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleGenerateInvoice = async () => {
        Swal.fire({
            title: "Generate Invoice",
            text: "Are you sure you want to generate the invoice for this Proforma Order?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, generate!",
            cancelButtonText: "Cancel",
        }).then(async (result) => {
            if (result.isConfirmed) {
                setSubmitting(true);
                try {
                    const res = await putData(`/proforma-orders/generate-invoice/${id}`, { comment });
                    if (res.status) {
                        toast.success(res.message);
                        navigate('/accounts-dashboard/view-edit');
                    } else {
                        toast.error(res.message || "Failed to generate invoice");
                    }
                } catch (error) {
                    console.error("Error generating invoice:", error);
                    toast.error(error.response?.data?.message || "Failed to generate invoice");
                } finally {
                    setSubmitting(false);
                }
            }
        });
    };

    const fetchOrderDetails = async () => {
        try {
            const res = await getData(`/proforma-orders/details/${id}`);
            if (res.status) {
                setOrder(res.data);
            }
        } catch (error) {
            console.error("Error fetching PO details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrderDetails();
    }, [id]);

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'invoiced': return 'bg-success';
            case 'ready_for_delivery': return 'bg-primary';
            case 'cancelled': return 'bg-danger';
            case 'checking': return 'bg-warning';
            default: return 'bg-secondary';
        }
    };

    if (loading) {
        return (
            <div className="pc-container">
                <div className="pc-content text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3 text-muted">Loading Proforma Order details...</div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="pc-container">
                <div className="pc-content">
                    <div className="alert alert-danger">Proforma Order not found or access denied.</div>
                    <Link to="/accounts-dashboard/view-edit" className="btn btn-outline-secondary">Go Back</Link>
                </div>
            </div>
        );
    }

    // Parse products if string
    let products = [];
    try {
        products = typeof order.products === 'string' ? JSON.parse(order.products) : (order.products || []);
    } catch (e) {
        console.error("Failed to parse products", e);
    }

    return (
        <div className="pc-container">
            <div className="pc-content">
                {/* Breadcrumbs */}
                <div className="page-header">
                    <div className="page-block">
                        <div className="row align-items-center">
                            <div className="col-md-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/accounts-dashboard/view-edit">Accounts</Link></li>
                                    <li className="breadcrumb-item" aria-current="page">PO Details</li>
                                </ul>
                            </div>
                            <div className="col-md-12">
                                <div className="page-header-title">
                                    <h2 className="mb-0">Proforma Order Details</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="row">
                    <div className="col-lg-4">
                        <div className="card">
                            <div className="card-header">
                                <h5>PO Info</h5>
                            </div>
                            <div className="card-body">
                                <ul className="list-group list-group-flush">
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">PO Number:</span>
                                        <span className="fw-bold">{order.po_number}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">PO Type:</span>
                                        <span className="fw-bold">{order.po_type || '-'}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Customer Name:</span>
                                        <span className="fw-bold">{order.customer_name}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Date & Time:</span>
                                        <span>{order.date} {order.time}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Status:</span>
                                        <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                                            {order.status.replace(/_/g, ' ').toUpperCase()}
                                        </span>
                                    </li>
                                    {order.invoice_number && (
                                        <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                            <span className="text-muted">Invoice Number:</span>
                                            <span className="fw-bold text-success">{order.invoice_number}</span>
                                        </li>
                                    )}
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Total Amount:</span>
                                        <span className="fw-bold text-primary fs-5">Tzs {Number(order.total_amount).toLocaleString('en-US')}</span>
                                    </li>
                                </ul>

                                <div className="d-grid gap-2 mt-4">
                                    {order.status !== 'invoiced' && (
                                        <Link to={`/accounts-dashboard/edit-po/${order.id}`} className="btn btn-success">
                                            <i className="fas fa-edit me-2" /> Edit PO
                                        </Link>
                                    )}
                                    <Link to="/accounts-dashboard/view-edit" className="btn btn-outline-secondary">
                                        <i className="fas fa-arrow-left me-2" /> Back to List
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-8">
                        <div className="card">
                            <div className="card-header">
                                <h5>Itemized Products</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Code</th>
                                                <th>Product</th>
                                                <th className="text-center">Qty</th>
                                                <th>Unit</th>
                                                <th className="text-end">Rate</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.length > 0 ? (
                                                products.map((prod, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{prod.code || '-'}</td>
                                                        <td className="fw-semibold">{prod.product || prod.name || '-'}</td>
                                                        <td className="text-center">{prod.qty || prod.quantity || 0}</td>
                                                        <td>{prod.unit || '-'}</td>
                                                        <td className="text-end">Tzs {Number(prod.rate || prod.price || 0).toLocaleString('en-US')}</td>
                                                        <td className="text-end fw-bold">Tzs {Number(prod.amount || ((prod.qty || prod.quantity || 0) * (prod.rate || prod.price || 0))).toLocaleString('en-US')}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4 text-muted">No products found in this Proforma Order.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {products.length > 0 && (
                                            <tfoot>
                                                <tr className="table-light">
                                                    <td colSpan="6" className="text-end fw-bold">Grand Total:</td>
                                                    <td className="text-end fw-bold text-primary fs-6">Tzs {Number(order.total_amount).toLocaleString('en-US')}</td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                        </div>

                        {order.status !== 'invoiced' && order.status !== 'cancelled' && (
                            <div className="card mt-4">
                                <div className="card-header">
                                    <h5>Invoice Comment</h5>
                                </div>
                                <div className="card-body">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold">Enter an optional comment/note to display at the bottom of this invoice:</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            placeholder="Type your comment here..."
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                        />
                                    </div>
                                    <div className="text-end">
                                        <button
                                            type="button"
                                            className="btn btn-primary px-4"
                                            disabled={submitting}
                                            onClick={handleGenerateInvoice}
                                        >
                                            {submitting ? 'Generating...' : 'Save & Submit'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewPO;
